import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Mail, Lock, Eye, EyeOff, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const LOGO_URL = "/logo-adegga.png";

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    // Validação de email simples
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error('Digite um email válido');
      return;
    }
    
    if (form.password !== form.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (form.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      // Register user
      await axios.post(`${API}/auth/register`, {
        name: form.name,
        email: form.email,
        password: form.password,
        role: 'store_admin'
      });
      
      // Auto login
      await login(form.email, form.password);
      toast.success('Conta criada com sucesso!');
      
      // Go to onboarding (store name will be set there)
      navigate('/app/onboarding');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao criar conta';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="ADEGGA DELIVERY" className="h-24 sm:h-28 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white">Crie sua Conta</h1>
          <p className="text-white/60 mt-2">Comece a vender em minutos</p>
        </div>

        <form onSubmit={handleSubmit} className="card-adegga p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm text-white/60">Seu Nome</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Seu nome completo"
                className="pl-10 bg-[#0f0f15] border-white/10 text-white"
                data-testid="register-name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-white/60">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                inputMode="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value.toLowerCase())}
                placeholder="seu@email.com"
                className="pl-10 bg-[#0f0f15] border-white/10 text-white"
                data-testid="register-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-white/60">Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="pl-10 pr-10 bg-[#0f0f15] border-white/10 text-white"
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-white/60">Confirmar Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Digite a senha novamente"
                className="pl-10 bg-[#0f0f15] border-white/10 text-white"
                data-testid="register-confirm-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full btn-primary h-12"
            disabled={loading}
            data-testid="register-submit"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              'Criar Conta'
            )}
          </Button>
        </form>

        <p className="text-center text-white/40 mt-6 text-sm">
          Já tem conta?{' '}
          <Link to="/app/login" className="text-[#ff6b00] hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
